import React, { useState } from 'react';
import { X } from 'lucide-react';
import { UnidadeConsumidora } from '../../types';
import UCSearchPicker from './UCSearchPicker';
import { adicionarParadaExtra } from '../../src/lib/rotaCobranca';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';

interface Props {
    rotaId: string;
    onClose: () => void;
    onAdicionada: () => void;
}

export default function ModalAdicionarParada({ rotaId, onClose, onAdicionada }: Props) {
    const { profile } = useAuth();
    const { addToast } = useToast();
    const [salvando, setSalvando] = useState(false);
    const [selecionada, setSelecionada] = useState<UnidadeConsumidora | null>(null);

    const handleSalvar = async () => {
        if (!selecionada || !profile?.id) return;
        setSalvando(true);
        try {
            await adicionarParadaExtra(rotaId, selecionada.id, profile.id);
            addToast('Parada adicionada à rota', 'success');
            onAdicionada();
        } catch (e: any) {
            addToast(e?.message || 'Erro ao adicionar parada', 'error');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Adicionar parada extra</h2>
                    <button onClick={onClose} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-xl transition-colors">
                        <X size={20} />
                    </button>
                </div>
                
                <div className="p-5 flex-1 min-h-[300px]">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Busque a unidade consumidora que deseja incluir na rota atual. Ela será adicionada no fim da lista de pendentes.
                    </p>
                    
                    <UCSearchPicker
                        selecionadas={selecionada ? [selecionada] : []}
                        onAdd={(uc) => setSelecionada(uc)}
                        onRemove={() => setSelecionada(null)}
                        max={1}
                        placeholder="Nome, UC ou CPF/CNPJ..."
                    />

                    {selecionada && (
                        <div className="mt-4 p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
                            <div>
                                <p className="text-sm font-bold text-gray-700 dark:text-gray-200">{selecionada.nome_cliente || selecionada.titular_fatura}</p>
                                <p className="text-xs text-gray-500 font-mono">UC: {selecionada.numero_uc}</p>
                            </div>
                            <button onClick={() => setSelecionada(null)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors">
                                <X size={16} />
                            </button>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        disabled={!selecionada || salvando}
                        onClick={handleSalvar}
                        className="px-6 py-2 rounded-xl text-sm font-bold text-white bg-[#5F6C37] hover:bg-[#4a5429] disabled:opacity-50 transition-colors"
                    >
                        {salvando ? 'Adicionando...' : 'Adicionar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
